import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SugarMarketplaceUpload = ({ onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const templateFields = [
    { field: 'Factory Name', required: true, example: 'Hira Sugars Limited' },
    { field: 'Mill Code', required: true, example: 'HIRA001' },
    { field: 'City', required: true, example: 'Pune' },
    { field: 'State', required: true, example: 'Maharashtra' },
    { field: 'Address', required: true, example: 'Survey No. 123, Village Road, Pune' },
    { field: 'Established Year', required: true, example: '1995' },
    { field: 'Capacity (TCD)', required: true, example: '5000' },
    { field: 'Current Production', required: true, example: '4500' },
    { field: 'Annual Capacity (MT)', required: true, example: '150000' },
    { field: 'Contact Person', required: true, example: 'John Doe' },
    { field: 'Phone', required: true, example: '+91 9876543210' },
    { field: 'Email', required: true, example: 'contact@hirasugars.com' },
    { field: 'Sugar Grades', required: true, example: 'S-30, S-31, M-30' },
    { field: 'Certifications', required: true, example: 'ISO 9001:2015, FSSAI, HACCP' },
    { field: 'Facilities', required: true, example: 'Warehousing, Quality Testing' },
    { field: 'Quality Standards', required: true, example: 'BIS, FSSAI, Export Quality' },
    { field: 'Rating', required: true, example: '4.8' },
    { field: 'Delivery Time', required: true, example: '2-3 days' },
    { field: 'Payment Terms', required: true, example: '50% Advance, 50% on delivery' },
    { field: 'Product Name', required: false, example: 'White Refined Sugar' },
    { field: 'Grade', required: false, example: 'S-30' },
    { field: 'Quantity (MT)', required: false, example: '500' },
    { field: 'Rate per Ton (₹)', required: false, example: '38500' },
    { field: 'Pack Size', required: false, example: '50 kg' },
    { field: 'Payment Date', required: false, example: '2025-12-15' },
    { field: 'Lifting Date', required: false, example: '2025-12-18' }
  ];

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const validateFile = (file) => {
    const errors = [];
    
    // File type validation
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes?.includes(file?.type)) {
      errors?.push('Invalid file type. Please upload CSV or Excel (.xlsx) files only.');
    }
    
    // File size validation (10MB max)
    if (file?.size > 10 * 1024 * 1024) {
      errors?.push('File size exceeds 10MB limit.');
    }
    
    if (errors?.length > 0) {
      setValidationErrors(errors);
      setSelectedFile(null);
    } else {
      setValidationErrors([]);
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          
          // Simulate successful upload
          const newHistoryEntry = {
            id: Date.now(),
            type: 'Sugar Marketplace',
            fileName: selectedFile?.name,
            uploadDate: new Date()?.toLocaleString('en-IN'),
            status: 'success',
            totalRecords: Math?.floor(Math?.random() * 200) + 50,
            successfulRecords: Math?.floor(Math?.random() * 190) + 45,
            failedRecords: Math?.floor(Math?.random() * 10),
            uploadedBy: 'Current User'
          };
          
          onUploadComplete?.(newHistoryEntry);
          setSelectedFile(null);
          
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const handleDownloadTemplate = () => {
    // In real implementation, this would download an actual template file
    alert('Downloading Sugar Marketplace template...\n\nThe template will include all mandatory and optional fields with example data.');
  };

  return (
    <div className="space-y-6">
      {/* Template Download Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Icon name="FileDown" size={24} className="text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-green-900 mb-1">
                Download Template First
              </h3>
              <p className="text-sm text-green-700 mb-3">
                Download the pre-formatted Excel/CSV template with all required fields and example data. 
                Fill in your factory and load offer details following the format.
              </p>
              <div className="bg-white rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Template includes:</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                  <li><strong>Factory Details:</strong> Name, location, capacity, certifications, facilities</li>
                  <li><strong>Contact Information:</strong> Person, phone, email</li>
                  <li><strong>Production Info:</strong> Grades, quality standards, delivery terms</li>
                  <li><strong>Load Offers (Optional):</strong> Product, grade, quantity, rate, dates</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            onClick={handleDownloadTemplate}
            className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
          >
            <Icon name="Download" size={18} />
            <span>Download Template</span>
          </Button>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging
            ? 'border-[#ff8c00] bg-orange-50'
            : 'border-gray-300 bg-white hover:border-[#ff8c00] hover:bg-orange-50/30'
        }`}
      >
        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="FileCheck" size={32} className="text-green-600" />
              <div className="text-left">
                <p className="font-semibold text-green-900">{selectedFile?.name}</p>
                <p className="text-sm text-green-700">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
            
            {isUploading && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] h-full transition-all duration-300 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
              >
                <Icon name="Upload" size={18} />
                <span>{isUploading ? 'Uploading...' : 'Upload File'}</span>
              </Button>
              <Button
                onClick={() => setSelectedFile(null)}
                variant="outline"
                disabled={isUploading}
              >
                <Icon name="X" size={18} />
                <span>Remove</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Icon name="Upload" size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Upload Sugar Marketplace Data
            </h3>
            <p className="text-gray-600 mb-4">
              Drag and drop your CSV or Excel file here, or click to browse
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef?.current?.click()}
              className="bg-[#ff8c00] hover:bg-[#e67e00] text-white"
            >
              <Icon name="FolderOpen" size={18} />
              <span>Select File</span>
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              Supported formats: CSV, XLSX • Maximum size: 10MB
            </p>
          </>
        )}
      </div>

      {/* Validation Errors */}
      {validationErrors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-2">Validation Errors</h4>
              <ul className="space-y-1">
                {validationErrors?.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    • {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Field Reference */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="List" size={18} className="text-[#ff8c00]" />
          Template Field Reference
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templateFields?.map((field, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {field?.field}
                    {field?.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Example: {field?.example}
                  </p>
                </div>
                {field?.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">Important Notes:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>All factory details (marked with *) are mandatory for each entry</li>
              <li>Load offer details are optional - you can add them later manually</li>
              <li>Multiple sugar grades, certifications, and facilities should be comma-separated</li>
              <li>Dates should be in YYYY-MM-DD format</li>
              <li>System will perform duplicate detection and validation</li>
              <li>You'll receive a detailed report after upload completion</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SugarMarketplaceUpload;